/**
 * AuthManager Module
 * Handles Google OAuth 2.0 Identity Services authentication,
 * JWT session storage, and route access control.
 */

const TOKEN_KEY = 'nfce_jwt_session';
const USER_KEY = 'nfce_user_profile';
const SCOPE_KEY = 'nfce_current_scope';

export function getSavedScope() {
  return localStorage.getItem(SCOPE_KEY) || 'personal';
}

export function setSavedScope(scope) {
  if (scope === 'family' || scope === 'personal') {
    localStorage.setItem(SCOPE_KEY, scope);
    window.dispatchEvent(new CustomEvent('nfce-scope-changed', { detail: { scope } }));
  }
}

export class AuthManager {
  constructor() {
    this.token = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || null;
    this.user = this.loadUser();
    this._googleClientId = null;
  }

  loadUser() {
    try {
      const raw = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  isAuthenticated() {
    return !!this.token;
  }

  isAdmin() {
    const user = this.getUser();
    return !!(user && user.isAdmin);
  }

  getUser() {
    if (!this.user) return null;
    const email = (this.user.email || '').toLowerCase();
    const isAdmin = !!this.user.isAdmin || email === 'carlospineli@gmail.com';
    return {
      ...this.user,
      isAdmin
    };
  }

  getToken() {
    return this.token;
  }

  getUserId() {
    return this.user ? (this.user.id || this.user.sub || '') : '';
  }

  /**
   * Make an HTTP request with the session Authorization header attached.
   */
  async fetchWithAuth(url, options = {}) {
    const headers = {
      ...(options.headers || {})
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    let effectiveUrl = url;
    const baseUrl = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL) ? import.meta.env.BASE_URL : '/';
    if (url.startsWith('/api') && baseUrl !== '/') {
      const cleanBase = baseUrl.replace(/\/$/, '');
      effectiveUrl = `${cleanBase}${url}`;
    }

    try {
      const res = await fetch(effectiveUrl, { ...options, headers });
      if (res.status === 401) {
        this.handleSessionExpired('Sessão expirada. Por favor, faça login novamente.');
      }
      return res;
    } catch (err) {
      throw err;
    }
  }

  handleSessionExpired(message = 'Sua sessão expirou. Faça login novamente.') {
    if (this._isHandlingExpiration) return;
    this._isHandlingExpiration = true;

    this.clearSession();

    // Trigger toast notification if available
    window.dispatchEvent(new CustomEvent('sessionexpired', { detail: { message } }));

    if (window.location.hash !== '#login') {
      window.location.hash = '#login';
    }

    setTimeout(() => {
      this._isHandlingExpiration = false;
    }, 2000);
  }

  /**
   * Validates current token against the backend.
   * Returns true if valid or offline, false if expired/unauthorized.
   */
  async verifySession() {
    if (!this.token) return false;
    if (!navigator.onLine) return true; // Offline mode preserved

    try {
      const res = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      if (res.status === 401) {
        this.handleSessionExpired('Sessão expirada no servidor. Por favor, faça login novamente.');
        return false;
      }
      if (res.ok) {
        const data = await res.json();
        if (data && data.user) {
          this.user = { ...this.user, ...data.user };
          const storage = localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage;
          storage.setItem(USER_KEY, JSON.stringify(this.user));
        }
        return true;
      }
      return false;
    } catch (err) {
      // Network glitch or server starting up: don't forcefully logout if network failed
      console.warn('verifySession network error:', err);
      return true;
    }
  }

  setSession(token, user, remember = true) {
    this.token = token;
    this.user = user;

    const storage = remember ? localStorage : sessionStorage;
    storage.setItem(TOKEN_KEY, token);
    storage.setItem(USER_KEY, JSON.stringify(user));

    window.dispatchEvent(new CustomEvent('authstatechanged', { detail: { authenticated: true, user } }));
  }

  clearSession() {
    this.token = null;
    this.user = null;

    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    window.dispatchEvent(new CustomEvent('authstatechanged', { detail: { authenticated: false, user: null } }));
  }

  /**
   * Fetch the Google Client ID from the backend config endpoint.
   * Caches the result for subsequent calls.
   */
  async getGoogleClientId() {
    if (this._googleClientId) return this._googleClientId;

    try {
      const res = await fetch('/api/auth/config');
      if (res.ok) {
        const data = await res.json();
        this._googleClientId = data.googleClientId || '';
        return this._googleClientId;
      }
    } catch (err) {
      console.warn('Failed to fetch auth config:', err);
    }
    return '';
  }

  /**
   * Send a Google ID Token to the backend for verification.
   * The backend validates the token with Google's public keys,
   * then issues a session JWT.
   */
  async verifyGoogleCredential(idToken) {
    try {
      const res = await fetch('/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken })
      });

      if (res.ok) {
        const data = await res.json();
        this.setSession(data.token, data.user);
        return { success: true, user: data.user };
      }

      const errData = await res.json().catch(() => ({}));
      return { success: false, error: errData.error || 'Authentication failed' };
    } catch (err) {
      console.error('Google auth verification error:', err);
      return { success: false, error: 'Servidor indisponível. Tente novamente.' };
    }
  }
}

export const authManager = new AuthManager();
